import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Button } from '../../ui/button'
import AvatarUpload from '../AvatarUpload/AvatarUpload'
import PhoneInput from '../PhoneInput/PhoneInput'

function ProfileForm({
  onSubmit,
  id,
  fullName,
  preferredName,
  email,
  phone,
  cloudinaryPhotoPublicId,
  protectedDemoLogin = false
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [photoPublicId, setPhotoPublicId] = useState(cloudinaryPhotoPublicId)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control
  } = useForm({
    defaultValues: {
      fullName: fullName || '',
      preferredName: preferredName || '',
      email: email || '',
      phone: phone || '',
      password: '',
      passwordConfirmation: ''
    }
  })

  const password = watch('password')

  function handleFormSubmit(data) {
    setIsSubmitting(true)
    const submitData = {
      full_name: data.fullName,
      preferred_name: data.preferredName,
      email: data.email,
      phone: data.phone,
      cloudinary_photo_public_id: photoPublicId
    }

    if (data.password) {
      submitData.password = data.password
      submitData.password_confirmation = data.passwordConfirmation
    }

    onSubmit(submitData)
      .then(() => {
        setValue('password', '')
        setValue('passwordConfirmation', '')
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  function handlePhotoUpload(newPublicId) {
    setPhotoPublicId(newPublicId)
  }

  function validateEmail(value) {
    if (!value) return 'Email is required'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value) || 'Please enter a valid email address'
  }

  function validatePhone(value) {
    if (!value) return true
    const e164Regex = /^\+?1?\d{10,}$/
    const formattedRegex = /^(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
    const digitsOnly = value.replace(/\D/g, '')
    return (
      e164Regex.test(value) ||
      formattedRegex.test(value) ||
      digitsOnly.length === 10 ||
      digitsOnly.length === 11 ||
      'Please enter a valid phone number'
    )
  }

  function validatePassword(value) {
    if (!value) return true
    return value.length >= 8 || 'Password must be at least 8 characters'
  }

  function validatePasswordConfirmation(value) {
    if (!password) return true
    return value === password || 'Passwords do not match'
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 lg:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4 lg:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="mt-1 text-sm text-gray-600">Manage your account information and security settings</p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your personal details and contact information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="fullName"
                        placeholder="Richard Button"
                        autoFocus
                        {...register('fullName', { required: 'Full name is required' })}
                        className={errors.fullName ? 'border-red-500' : ''}
                      />
                      {errors.fullName && <p className="text-sm text-red-600">{errors.fullName.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferredName">
                        Preferred Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="preferredName"
                        placeholder="Richard"
                        {...register('preferredName', { required: 'Preferred name is required' })}
                        className={errors.preferredName ? 'border-red-500' : ''}
                      />
                      {errors.preferredName && <p className="text-sm text-red-600">{errors.preferredName.message}</p>}
                    </div>
                  </div>

                  <p className="text-sm text-gray-500">This is how coaches and directors will address you</p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        readOnly={protectedDemoLogin}
                        type="email"
                        placeholder="richard@example.com"
                        {...register('email', { validate: validateEmail })}
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Controller
                        name="phone"
                        control={control}
                        rules={{ validate: validatePhone }}
                        render={({ field }) => (
                          <PhoneInput
                            id="phone"
                            label="Phone Number"
                            placeholder="(555) 555-5555"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            errorMessage={errors.phone?.message}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <p className="text-sm text-gray-500">We&apos;ll use this to send you important notifications</p>
                </div>

                <div className="lg:w-72 flex-shrink-0">
                  <div className="lg:sticky lg:top-6">
                    <div className="border-l border-gray-200 pl-6">
                      <AvatarUpload
                        compact
                        cloudinaryPhotoPublicId={photoPublicId}
                        onPhotoUpload={handlePhotoUpload}
                        fullName={watch('fullName') || fullName}
                        userId={id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Security</CardTitle>
              <CardDescription>Update your password to keep your account secure</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    disabled={protectedDemoLogin}
                    type="password"
                    placeholder="••••••••"
                    {...register('password', { validate: validatePassword })}
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  {errors.password && <p className="text-sm text-red-600">{errors.password.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passwordConfirmation">Confirm New Password</Label>
                  <Input
                    id="passwordConfirmation"
                    type="password"
                    placeholder="••••••••"
                    {...register('passwordConfirmation', { validate: validatePasswordConfirmation })}
                    className={errors.passwordConfirmation ? 'border-red-500' : ''}
                    disabled={protectedDemoLogin || !password}
                  />
                  {errors.passwordConfirmation && (
                    <p className="text-sm text-red-600">{errors.passwordConfirmation.message}</p>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-500">Password must be at least 8 characters long</p>

              {password && (
                <div className="rounded-md bg-blue-50 p-4">
                  <p className="text-sm text-blue-700">
                    Leave both password fields empty if you don&apos;t want to change your password
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting}
              className="bg-purple-600 hover:bg-purple-700 min-w-[140px]"
            >
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

ProfileForm.propTypes = {
  protectedDemoLogin: PropTypes.bool,
  fullName: PropTypes.string,
  preferredName: PropTypes.string,
  cloudinaryPhotoPublicId: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default ProfileForm
